import {
  Component,
  Input,
  ContentChildren,
  QueryList,
  AfterViewInit,
  ElementRef,
  Output,
  EventEmitter,
  ViewEncapsulation,
  SimpleChanges,
  OnChanges,
  HostBinding,
  Renderer2,
  AfterViewChecked,
} from '@angular/core';

import { PgLinearLoaderMessageComponent } from './pg-linear-loader-message.component';

import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';

import { LinearLoaderStyles } from './linear-loader-styles';
import { clamp } from '../core/math/clamp';

export interface ProgressAnimationEnd {
  value: number;
}

let progressbarId = 0;

@Component({
  selector: 'pg-linear-loader, grit-linear-loader',
  templateUrl: './pg-linear-loader.component.html',
  styleUrls: ['./pg-linear-loader.component.scss'],
  exportAs: 'pgLinearLoader, gritLinearLoader',
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('transitionLoaderVisible', [
      state(
        'visible',
        style({
          transform: 'scaleY(1)',
        })
      ),
      state(
        'hidden',
        style({
          transform: 'scaleY(0)',
        })
      ),
      transition('hidden <=> visible', animate('150ms')),
    ]),
    trigger('transitionProgressAmount', [
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'translateY(0)',
        })
      ),
      state(
        'hidden',
        style({
          opacity: 0,
          transform: 'translateY(-16px)',
        })
      ),
      transition('hidden <=> visible', animate('150ms')),
    ]),
  ],
})
export class PgLinearLoaderComponent
  implements AfterViewInit, OnChanges, AfterViewChecked {
  progressbarId = `pg-progress-bar-${progressbarId++}`;
  @Input() mode: 'determinate' | 'indeterminate' = 'indeterminate';
  @ContentChildren(PgLinearLoaderMessageComponent)
  private _messageChildren: QueryList<PgLinearLoaderMessageComponent>;
  @Input()
  get value(): number {
    return this._value;
  }

  set value(v: number) {
    if (v != this._value) {
      this._value = clamp(v || 0, 0, 100);
    }
  }

  private _value: number = 0;

  public _currentMessage: PgLinearLoaderMessageComponent;
  public messageArray: Array<PgLinearLoaderMessageComponent>;

  public progress = 0;

  public _currentIndex: number = 0;

  public _loaderRunning = true;

  public loaderVisibilityState = 'visible';

  public _progressIndicatorVisibility: 'visible' | 'hidden' = 'hidden';

  @HostBinding('class.grit-progress-bar')
  @HostBinding('class.pg-progress-bar')
  _classProgresBar = true;

  @Input()
  get hidden(): boolean {
    return this._isHidden;
  }

  set hidden(value: boolean) {
    if (value != this._isHidden) {
      this._isHidden = value;
    }
  }

  public _isHidden = false;

  @Output()
  loaderHidden = new EventEmitter<void>();

  @Output()
  messageChange = new EventEmitter<void>();

  get loaderRunning() {
    return this._loaderRunning;
  }

  set loaderRunning(val: boolean) {
    this._loaderRunning = val;
  }

  @Input() styleOverides: any;

  constructor(public _elementRef: ElementRef, public renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hidden) {
      if (
        (changes.hidden.currentValue === false &&
          changes.hidden.previousValue === true) ||
        (changes.hidden.currentValue === false &&
          changes.hidden.previousValue == null)
      ) {
        this.loaderVisibilityState = 'visible';
        this._loaderRunning = true;
      }

      // only run if changes are mode changes.
      if (
        (this.mode === 'determinate' && changes.mode) ||
        (changes.hidden.currentValue == false &&
          changes.hidden.previousValue == true)
      ) {
        // Gives some time before setting visibility to true
        // for the determinate loader
        // This is so the 0-100% text can wait to come in at
        // the appropriate time!
        setTimeout(() => {
          this._progressSteadyIncrease();
          this._progressIndicatorVisibility = 'visible';
        }, 3000);
      }
    }
  }

  getLargestHeight = () => {
    let largestHeight = 0;
    for (let i = 0; i < this.messageArray.length; i++) {
      let messages = this.messageArray[i]._elementRef.nativeElement.children;
      let messageCount = messages.length;
      for (let k = 0; k < messageCount; k++) {
        let aMessage = this.messageArray[i]._elementRef.nativeElement.children[
          k
        ];
        if (aMessage.offsetHeight > largestHeight) {
          largestHeight = aMessage.offsetHeight;
        }
      }
    }
    return largestHeight;
  };

  ngAfterViewInit() {
    this.messageArray = this._messageChildren.toArray();
    if (this.hidden) {
      setTimeout(() => {
        this.loaderVisibilityState = 'hidden';
        this._loaderRunning = false;
      });
    }
    if (this.messageArray.length) {
      this._currentMessage = this.messageArray[0];
    }
  }

  ngAfterViewChecked() {
    this.renderer.setStyle(
      this._elementRef.nativeElement,
      'height',
      `${this.getLargestHeight() + 100}px`
    );
  }

  _primaryTransform() {
    const scale = this.value / 100;
    return { transform: `scaleX(${scale})` };
  }

  handleLoaderAnimationDone($event) {
    if ($event.toState === 'visible') {
      this._runLinearLoader();
    } else if ($event.toState === 'hidden') {
      if (this._currentMessage) {
        this._removeMessage();
      }
      this.loaderHidden.emit();
      this._resetLoader();
    }
  }

  _resetLoader() {
    if (
      (this.messageArray && this.messageArray.length) ||
      this._currentMessage
    ) {
      this._currentMessage = this.messageArray[0];
      this._currentIndex = 0;
    }

    if (this.mode === 'determinate') {
      setTimeout(() => {
        this.value = 0;
      }, 100);

      this._progressIndicatorVisibility = 'hidden';
    }
  }

  checkLoaderStatus() {
    if (this.hidden) {
      this.loaderVisibilityState = 'hidden';
      this.loaderRunning = false;
    } else {
      if (
        this.messageArray &&
        this._currentMessage &&
        this._currentMessage !== this.messageArray[this._currentIndex]
      ) {
        this._currentMessage = this.messageArray[this._currentIndex];
      }
      this._runLinearLoader();
    }
  }

  public _removeMessage() {
    this._currentMessage.messageVisibilityState = 'hidden';
  }

  public _addMessage() {
    this._currentMessage.messageVisibilityState = 'visible';
  }

  private async _runLinearLoader() {
    try {
      if (!this.messageArray || !this._currentMessage) {
        await this._runLoaderLineAnimationCycles(2);
        this.checkLoaderStatus();
      } else if (this._currentMessage.delay > 0) {
        await this._runLoaderLineAnimationCycles(this._currentMessage.delay);
        if (!this.hidden) {
          this._addMessage();
        }

        //last message
        if (
          this._currentMessage ===
          this.messageArray[this.messageArray.length - 1]
        ) {
          await this._runLoaderLineAnimationCycles(
            this._currentMessage.duration
          );
          this.checkLoaderStatus();
        } else {
          await this._runLoaderLineAnimationCycles(
            this._currentMessage.duration
          );
          if (!this.hidden) {
            this._removeMessage();
            this._currentIndex++;
            this.checkLoaderStatus();
          }
        }
        // If not the last message and no delay
      } else if (
        this._currentMessage !==
          this.messageArray[this.messageArray.length - 1] &&
        this._currentMessage.delay === 0
      ) {
        this._addMessage();
        await this._runLoaderLineAnimationCycles(this._currentMessage.duration);
        if (!this.hidden) {
          this._removeMessage();
          this._currentIndex++;

          this.checkLoaderStatus();
        }
      } else if (
        this._currentMessage ===
          this.messageArray[this.messageArray.length - 1] &&
        this._currentMessage.delay === 0
      ) {
        this._addMessage();
        await this._runLoaderLineAnimationCycles(this._currentMessage.duration);
        if (!this.hidden) {
          this.checkLoaderStatus();
        }
      }
    } catch (e) {}
  }

  private async _runLoaderLineAnimationCycles(count = 1) {
    let total = 0;
    return new Promise((res, rej) => {
      const execute = () => {
        total++;
        setTimeout(() => {
          // If the total is less than the count arg
          // or count is -1 and !this.hidden
          // execute this function again.
          if (!this.hidden && (total < count || count < 0)) {
            execute();
          } else {
            if (this.hidden && this.mode === 'indeterminate') {
              this.loaderVisibilityState = 'hidden';
              this.loaderRunning = false;
            } else if (this.hidden && this.mode === 'determinate') {
              this._progressBurst(100 - this.value);
              setTimeout(() => {
                this.loaderVisibilityState = 'hidden';
                this.loaderRunning = false;
              }, (100 - this.value) * 10 + 1000);
            }
            if (this.value < 70) {
              this._progressBurst();
            }

            res();
          }
        }, 3000);
      };
      if (!this.hidden) {
        execute();
      }
    });
  }

  private async _progressBurst(count = 15) {
    let duration = 20;
    let total = 0;
    return new Promise((res, rej) => {
      const execute = () => {
        this.value++;
        total++;
        setTimeout(() => {
          if (this.value < 100 && total < count) {
            execute();
          } else {
            res();
          }
        }, duration);
      };

      execute();
    });
  }

  public async _progressSteadyIncrease() {
    return new Promise((res, rej) => {
      const execute = () => {
        let duration = 700;
        this.value++;
        setTimeout(() => {
          if (this.value < 70 && !this.hidden) {
            execute();
          } else {
            res();
          }
        }, duration);
      };
      if (!this.hidden) {
        execute();
      }
    });
  }
}
